import debounce from 'lodash/debounce'
import { memo, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useFeatureFlags } from '@opallabs/api'
import { UserToken } from '@opallabs/beacon'
import { CalendarPageHeader } from '@opallabs/calendar'
import { Timescale } from '@opallabs/concepts'
import { Theme, ZIndex } from '@opallabs/gem'
import { MessageKeys, useWorkspace, useBackboneUserRole } from '@opallabs/hooks'
import { kuiperClassNames, openDrawer } from '@opallabs/kuiper'
import {
  Button,
  ButtonIconPosition,
  Icon,
  DropdownMenu,
} from '@opallabs/lapidary'
import { State } from '@opallabs/wormhole'
import { WeekViewMode } from '../WeekView/types'
import {
  buildFilterTreeMap,
  getSelectedFilterParams,
} from '../WeekView/WeekViewDayHeader/utils'
import { useWeekViewSettings } from '../WeekView/WeekViewSettingsContext'
import { inMarketPreviewRoute } from './inMarketPreviewRoute'
import ModePicker from './ModePicker'
import * as styles from './styles'

const SEARCH_DELAY = 750

interface HeaderProps {
  createPresentation: () => void
  hasUnseenNotification: boolean
  /** What the search box holds when it is drawn. */
  initialSearchTerm?: string
  isExportPopoverOpen: boolean
  mode: Timescale
  onActionsMenuSelect?: (option: { id: 'insights' | 'export_csv' }) => void
  onClickCreateMomentButton?: () => void
  onExportPopoverConfirm: () => void
  onModeChange: (timescale: Timescale) => void
  onSearch: (searchTerm: string) => void
  onShareClick?: () => void
  /**
   * The filters the in-market preview opens under. Leave it out and it opens
   * under the week's filters.
   */
  previewFilterParams?: Record<string, string>
  /** The days the in-market preview opens on: whatever the view is showing. */
  previewRange: { start: Date; end: Date }
  theme: Theme
}

const Header: React.FC<HeaderProps> = ({
  createPresentation,
  hasUnseenNotification,
  initialSearchTerm,
  mode = Timescale.Week,
  onActionsMenuSelect,
  onClickCreateMomentButton,
  onExportPopoverConfirm,
  onModeChange,
  onSearch,
  onShareClick,
  previewFilterParams,
  previewRange,
  isExportPopoverOpen,
  theme = 'light',
}) => {
  const { data: featureFlags } = useFeatureFlags()
  const { slug } = useWorkspace()
  const { roleIsNot } = useBackboneUserRole()
  const filterTreeMap = useSelector((state: State) =>
    buildFilterTreeMap(state.filters.filterTree)
  )
  const selectedFilters = useSelector(
    (state: State) => state.filters.selectedFilters || {}
  )
  const selectedFilterParams = getSelectedFilterParams(
    filterTreeMap,
    selectedFilters
  )
  const { viewMode } = useWeekViewSettings()
  const isContentModeInWeek =
    mode === Timescale.Week && viewMode === WeekViewMode.Content
  const actionsAvailable =
    featureFlags?.insights || featureFlags?.export_capabilities || onShareClick

  const debouncedSearch = useMemo(
    () => debounce(onSearch, SEARCH_DELAY),
    [onSearch]
  )

  // The header is drawn again to show a keyword from the address, so a search
  // still waiting in this one would land after it and put the old text back.
  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch])

  const navigateToInMarketPreview = () => {
    /**
     * We use postMessage here to navigate the asgard router without triggering the
     * backbone router so the backbone view does not re-render needlessly.
     */
    parent.postMessage(
      {
        message: MessageKeys.execute,
        route: inMarketPreviewRoute(
          slug,
          previewRange.start,
          previewRange.end,
          previewFilterParams ?? selectedFilterParams
        ),
        replace: false,
      },
      '*'
    )
  }

  return (
    <CalendarPageHeader
      title="Calendar"
      search={{
        initialValue: initialSearchTerm,
        labelText: 'Search calendar',
        onSearch: debouncedSearch,
        placeholder: 'Search calendar…',
      }}
      center={<ModePicker {...{ mode, onModeChange }} />}
      actions={
        <>
          {actionsAvailable && (
            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <Button
                  ariaHasPopup={true}
                  name="More actions"
                  buttonText="More actions…"
                  variant="ghost"
                  hideButtonText={true}
                  icon={<Icon ariaMode="hidden" glyph="gem-more-horizontal" />}
                  theme={theme}
                />
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content
                  css={styles.dropdownContent}
                  align="end"
                  zIndex={ZIndex.Shroud.Popover}
                >
                  {/* The calendar presents whatever it is showing, story or
                      no story, so the menu offers it wherever the header's own
                      Present button would. */}
                  {roleIsNot('view_only') && (
                    <DropdownMenu.Item onSelect={createPresentation}>
                      <Icon
                        css={styles.dropdownItemIcon}
                        ariaHidden
                        glyph="gem-presentations"
                      />
                      Create Presentation
                    </DropdownMenu.Item>
                  )}
                  <DropdownMenu.Item onSelect={navigateToInMarketPreview}>
                    <Icon
                      css={styles.dropdownItemIcon}
                      ariaHidden
                      glyph="gem-eye"
                    />
                    See in-market preview
                  </DropdownMenu.Item>
                  {featureFlags?.insights && (
                    <DropdownMenu.Item
                      onSelect={() => onActionsMenuSelect?.({ id: 'insights' })}
                    >
                      <Icon
                        css={styles.dropdownItemIcon}
                        ariaHidden
                        glyph="gem-piechart"
                      />
                      View Insights
                    </DropdownMenu.Item>
                  )}
                  {featureFlags?.export_capabilities && (
                    <DropdownMenu.Item
                      onSelect={() =>
                        onActionsMenuSelect?.({ id: 'export_csv' })
                      }
                    >
                      <Icon
                        css={styles.dropdownItemIcon}
                        ariaHidden
                        glyph="gem-download"
                      />
                      Export to CSV
                    </DropdownMenu.Item>
                  )}
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          )}

          <Button
            ariaLabel="Preview"
            buttonText="Preview"
            icon={<Icon ariaMode="hidden" glyph="gem-eye" />}
            iconPosition={ButtonIconPosition.leading}
            name="preview"
            onClick={navigateToInMarketPreview}
            variant="tertiary"
            {...{
              theme,
            }}
          />

          {roleIsNot('view_only') &&
            (onShareClick ? (
              <Button
                ariaLabel="Share"
                name="share"
                buttonText="Share"
                variant="primary"
                icon={<Icon ariaMode="hidden" glyph="gem-locked" />}
                iconPosition={ButtonIconPosition.leading}
                onClick={onShareClick}
                {...{
                  theme,
                }}
              />
            ) : (
              <Button
                ariaLabel="Present"
                ariaPressed={false}
                variant="tertiary"
                name="create presentation"
                buttonText="Present"
                className="btn_share_presentation"
                icon={
                  <Icon
                    ariaMode="hidden"
                    className="kuiper-tokenIcon"
                    glyph="gem-presentations"
                  />
                }
                iconPosition={ButtonIconPosition.leading}
                onClick={() => createPresentation()}
                {...{
                  theme,
                }}
              />
            ))}

          {roleIsNot('limited', 'view_only') && !isContentModeInWeek && (
            /* @note Marionette month view has a 'Create a Moment' button on
                each day. The button assumes there's a selector matching
                `.btn_create_moment`.

                Hidden in Content mode: users create content via the per-cell
                affordance in `ContentStack`, not a global create button. */
            <Button
              ariaExpanded={false}
              ariaHasPopup={true}
              ariaLabel="Create"
              ariaPressed={false}
              name="create a moment"
              buttonText="Create"
              variant="secondary"
              className="btn_create_moment"
              icon={
                <Icon ariaMode="hidden" glyph="gem-add" useHypernova={false} />
              }
              iconPosition={ButtonIconPosition.leading}
              onClick={onClickCreateMomentButton}
              {...{
                theme,
              }}
            />
          )}

          {window.embedded && (
            <div className={kuiperClassNames('avatar-container')}>
              <UserToken
                avatarOverrideProps={{
                  size: 32,
                  onClick: () => openDrawer('darwin'),
                }}
                hasUnseenNotification={hasUnseenNotification}
                isExportPopoverOpen={isExportPopoverOpen}
                onExportPopoverConfirm={onExportPopoverConfirm}
                {...{
                  theme,
                }}
              />
            </div>
          )}
        </>
      }
    />
  )
}
Header.displayName = 'Continuum: Header'

export default memo(Header)
